import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DateTimeInput from '../../../../components/inputs/DateTimeInput';
import dayjs, { Dayjs } from 'dayjs';
import { validateOfferAgainstSchema } from '../../schemas/offerCreationSchema';
import { OfferReview } from '../../../../models/mailOfferReview';

interface OfferFormProps {
  offer: OfferReview;
  onChange: (updatedOffer: OfferReview) => void;
}

const OfferForm: React.FC<OfferFormProps> = ({ offer, onChange }) => {
  const { t } = useTranslation();

  // Use schema-based validation
  const validationErrors = validateOfferAgainstSchema(offer, t);

  const handleFieldChange = React.useCallback((path: string, value: any) => {
    const keys = path.split('.');
    const updatedOffer = JSON.parse(JSON.stringify(offer)); // Deep clone
    
    let current = updatedOffer;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    onChange(updatedOffer);
  }, [offer, onChange]);

  const handleArrayFieldChange = React.useCallback((arrayPath: string, index: number, field: string, value: any) => {
    const updatedOffer = JSON.parse(JSON.stringify(offer));
    const keys = arrayPath.split('.');
    
    let current = updatedOffer;
    for (const key of keys) {
      current = current[key];
    }
    
    if (current[index]) {
      const fieldKeys = field.split('.');
      let target = current[index];
      for (let i = 0; i < fieldKeys.length - 1; i++) {
        if (!target[fieldKeys[i]]) {
          target[fieldKeys[i]] = {};
        }
        target = target[fieldKeys[i]];
      }
      target[fieldKeys[fieldKeys.length - 1]] = value;
    }
    
    onChange(updatedOffer);
  }, [offer, onChange]);

  const loadingPlace = offer?.loadingPlaces?.[0] || {};
  const unloadingPlace = offer?.loadingPlaces?.[1] || {};

  return (
    <Box sx={{ maxHeight: 'calc(90vh - 300px)', overflow: 'auto', pr: 1 }}>
      {/* Geocode error if distance is 0 or undefined */}
      {(!offer?.distanceKms) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('Could not geocode the route from loading place to unloading place.')}
          </Typography>
          <Typography variant="caption">
            {t('Please verify the loading and unloading place information (city, country, postal code).')}
          </Typography>
        </Alert>
      )}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('Please fix the following errors:')}
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {validationErrors.map((error, idx) => (
              <li key={idx}>
                <Typography variant="caption">{error}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}
      
      {/* Contact Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('Contact Information')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
          <TextField
            fullWidth
            label={t('Contact Email')}
            value={offer?.contactPerson?.email || ''}
            onChange={(e) => handleFieldChange('contactPerson.email', e.target.value)}
            size="small"
            required
            error={validationErrors.some(err => err.includes('Contact Email'))}
          />
        </Box>
      </Box>

      {/* Freight Details */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('Freight Details')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <TextField
              fullWidth
              label={t('Freight Description')}
              value={offer?.freightDescription || ''}
              onChange={(e) => handleFieldChange('freightDescription', e.target.value)}
              size="small"
              multiline
              rows={2}
            />
          </Box>
          <TextField
            fullWidth
            label={t('Length (m)')}
            type="number"
            value={offer?.lengthM ?? ''}
            onChange={(e) => handleFieldChange('lengthM', e.target.value ? parseFloat(e.target.value) : undefined)}
            size="small"
          />
          <TextField
            fullWidth
            label={t('Weight (t)')}
            type="number"
            value={offer?.weightT ?? ''}
            onChange={(e) => handleFieldChange('weightT', e.target.value ? parseFloat(e.target.value) : undefined)}
            size="small"
          />
          <TextField
            fullWidth
            label={t('Client Price')}
            type="number"
            value={offer?.fePrice ?? ''}
            onChange={(e) => handleFieldChange('fePrice', e.target.value ? parseFloat(e.target.value) : undefined)}
            size="small"
          />
          <TextField
            fullWidth
            label={t('Distance (km)')}
            type="number"
            value={offer?.distanceKms || 0}
            onChange={(e) => handleFieldChange('distanceKms', parseFloat(e.target.value) || 0)}
            size="small"
            disabled
          />
        </Box>
      </Box>

      {/* Loading Place */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('Loading Place')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
          <TextField
            fullWidth
            label={t('City')}
            value={loadingPlace?.address?.city || ''}
            onChange={(e) => handleArrayFieldChange('loadingPlaces', 0, 'address.city', e.target.value)}
            size="small"
            error={validationErrors.some(err => err.includes('Loading Place City'))}
          />
          <TextField
            fullWidth
            label={t('Country')}
            value={loadingPlace?.address?.country || ''}
            onChange={(e) => handleArrayFieldChange('loadingPlaces', 0, 'address.country', e.target.value)}
            size="small"
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            error={validationErrors.some(err => err.includes('Loading Place Country'))}
          />
          <TextField
            fullWidth
            label={t('Postal Code')}
            value={loadingPlace?.address?.postalCode || ''}
            onChange={(e) => handleArrayFieldChange('loadingPlaces', 0, 'address.postalCode', e.target.value)}
            size="small"
            error={validationErrors.some(err => err.includes('Loading Place Postal Code'))}
          />
          <DateTimeInput
            type="datetime"
            label={t('Earliest Date')}
            value={loadingPlace?.earliestLoadingDate ? dayjs(loadingPlace.earliestLoadingDate) : null}
            onChange={(date: Dayjs | null) => handleArrayFieldChange('loadingPlaces', 0, 'earliestLoadingDate', date ? date.toISOString() : null)}
            size="small"
            fullWidth
            error={validationErrors.some(err => err.includes('Earliest Start'))}
          />
          <DateTimeInput
            type="datetime"
            label={t('Latest Date')}
            value={loadingPlace?.latestLoadingDate ? dayjs(loadingPlace.latestLoadingDate) : null}
            onChange={(date: Dayjs | null) => handleArrayFieldChange('loadingPlaces', 0, 'latestLoadingDate', date ? date.toISOString() : null)}
            size="small"
            fullWidth
            error={validationErrors.some(err => err.includes('Latest Start'))}
          />
        </Box>
      </Box>

      {/* Unloading Place */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('Unloading Place')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
          <TextField
            fullWidth
            label={t('City')}
            value={unloadingPlace?.address?.city || ''}
            onChange={(e) => handleArrayFieldChange('loadingPlaces', 1, 'address.city', e.target.value)}
            size="small"
            error={validationErrors.some(err => err.includes('Unloading Place City'))}
          />
          <TextField
            fullWidth
            label={t('Country')}
            value={unloadingPlace?.address?.country || ''}
            onChange={(e) => handleArrayFieldChange('loadingPlaces', 1, 'address.country', e.target.value)}
            size="small"
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            error={validationErrors.some(err => err.includes('Unloading Place Country'))}
          />
          <TextField
            fullWidth
            label={t('Postal Code')}
            value={unloadingPlace?.address?.postalCode || ''}
            onChange={(e) => handleArrayFieldChange('loadingPlaces', 1, 'address.postalCode', e.target.value)}
            size="small"
            error={validationErrors.some(err => err.includes('Unloading Place Postal Code'))}
          />
          <DateTimeInput
            type="datetime"
            label={t('Earliest Date')}
            value={unloadingPlace?.earliestLoadingDate ? dayjs(unloadingPlace.earliestLoadingDate) : null}
            onChange={(date: Dayjs | null) => handleArrayFieldChange('loadingPlaces', 1, 'earliestLoadingDate', date ? date.toISOString() : null)}
            size="small"
            fullWidth
            error={validationErrors.some(err => err.includes('Earliest End'))}
          />
          <DateTimeInput
            type="datetime"
            label={t('Latest Date')}
            value={unloadingPlace?.latestLoadingDate ? dayjs(unloadingPlace.latestLoadingDate) : null}
            onChange={(date: Dayjs | null) => handleArrayFieldChange('loadingPlaces', 1, 'latestLoadingDate', date ? date.toISOString() : null)}
            size="small"
            fullWidth
            error={validationErrors.some(err => err.includes('Latest End'))}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default OfferForm;


